import { useEffect, useState } from 'react'

// Simple YAML parser for news.yml
function parseNewsYAML(yamlText) {
  try {
    const lines = yamlText.split('\n')
    const result = { news: [] }
    let currentItem = null
    let inNewsArray = false

    for (let line of lines) {
      const trimmed = line.trim()

      if (trimmed === 'news:') {
        inNewsArray = true
        continue
      }

      if (!inNewsArray) continue

      if (trimmed.startsWith('- content:')) {
        if (currentItem) result.news.push(currentItem)
        currentItem = { content: extractQuotedValue(trimmed.substring(10)) }
      } else if (trimmed.startsWith('type:') && currentItem) {
        currentItem.type = extractQuotedValue(trimmed.substring(5))
      } else if (trimmed.startsWith('start_date:') && currentItem) {
        currentItem.start_date = extractQuotedValue(trimmed.substring(11))
      } else if (trimmed.startsWith('end_date:') && currentItem) {
        currentItem.end_date = extractQuotedValue(trimmed.substring(9))
      } else if (trimmed.startsWith('url:') && currentItem) {
        const urlValue = extractQuotedValue(trimmed.substring(4))
        currentItem.url = urlValue === 'null' ? null : urlValue
      }
    }

    if (currentItem) result.news.push(currentItem)
    return result
  } catch (error) {
    console.error('Error parsing YAML:', error)
    return null
  }
}

function extractQuotedValue(str) {
  const trimmed = str.trim()
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1)
  }
  return trimmed
}

function filterActiveNews(newsItems) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return newsItems.filter(item => {
    if (!item.start_date || !item.end_date) return false

    const startDate = new Date(item.start_date)
    const endDate = new Date(item.end_date)
    startDate.setHours(0, 0, 0, 0)
    endDate.setHours(23, 59, 59, 999)

    return today >= startDate && today <= endDate
  })
}

function hashString(str) {
  let hash = 0
  if (str.length === 0) return hash
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return hash.toString()
}

function NewsBannerItem({ newsItem, onDismiss }) {
  const isUnclosable = newsItem.type === 'warning'

  const bgColorClass = {
    warning: 'bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/50 dark:to-yellow-800/50 border-yellow-300 dark:border-yellow-700 text-yellow-900 dark:text-yellow-200',
    info: 'bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/50 border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-200',
    success: 'bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/50 dark:to-green-800/50 border-green-300 dark:border-green-700 text-green-900 dark:text-green-200'
  }[newsItem.type] || 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/50 dark:to-gray-700/50 border-gray-300 dark:border-gray-600'

  const hasUrl = newsItem.url && newsItem.url !== 'null'

  return (
    <div className={`relative border rounded-lg mx-4 my-2 px-6 py-4 shadow-sm ${bgColorClass}`}>
      {!isUnclosable && (
        <button
          onClick={onDismiss}
          className="absolute top-2 left-3 text-current/60 hover:text-current transition-colors text-xl leading-none"
        >
          &times;
        </button>
      )}
      <p className="text-sm leading-relaxed">
        {hasUrl ? (
          <a
            href={newsItem.url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline font-medium"
          >
            {newsItem.content}
          </a>
        ) : (
          newsItem.content
        )}
      </p>
    </div>
  )
}

export default function NewsBanner() {
  const [news, setNews] = useState([])

  useEffect(() => {
    const loadNews = async () => {
      try {
        const response = await fetch('/news.yml')
        if (!response.ok) return

        const yamlText = await response.text()
        const parsed = parseNewsYAML(yamlText)

        if (parsed && parsed.news && parsed.news.length > 0) {
          const activeNews = filterActiveNews(parsed.news)

          // Filter out dismissed news
          const dismissedNews = JSON.parse(localStorage.getItem('dismissed_news') || '[]')
          const visibleNews = activeNews.filter(item => {
            const newsHash = hashString(item.content)
            return item.type === 'warning' || !dismissedNews.includes(newsHash)
          })

          setNews(visibleNews)
        }
      } catch (error) {
        console.error('Error loading news:', error)
      }
    }

    loadNews()
  }, [])

  const dismissNews = (newsItem) => {
    const newsHash = hashString(newsItem.content)
    const dismissedNews = JSON.parse(localStorage.getItem('dismissed_news') || '[]')

    if (!dismissedNews.includes(newsHash)) {
      dismissedNews.push(newsHash)
      localStorage.setItem('dismissed_news', JSON.stringify(dismissedNews))
    }

    setNews(prev => prev.filter(item => hashString(item.content) !== newsHash))
  }

  if (news.length === 0) return null

  return (
    <div>
      {news.map((newsItem, index) => (
        <NewsBannerItem
          key={index}
          newsItem={newsItem}
          onDismiss={() => dismissNews(newsItem)}
        />
      ))}
    </div>
  )
}
